import React, { useEffect, useState } from 'react';
import ItemAdder from './ItemAdder';
import useHolidayAddons from '../../../../hooks/useHolidayAddons';
import useHolidayPricelist from '../../../../hooks/useHolidayPricelist';
import useHolidayPoses from '../../../../hooks/useHolidayPoses';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import { useCustomer } from '../../../../contexts/CustomerContext';
import styles from './HolidayBuilder.module.scss';
import useQueryString from '../../../../hooks/use-query-string';
import useStudent from '../../../../hooks/useStudent';
import useHolidayCoupon from '../../../../hooks/useHolidayCoupon';
import useCart from '../../../../hooks/useCart';
import { useHistory } from 'react-router-dom';
import { AddonSheet, OrderPackage } from '../../../../models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import routes from '../../../../routes';
import useOrderPackage from '../../../../hooks/useOrderPackage';
import QUERY_KEYS from '../../../../utils/QueryKeys';
import Button from "../../../Button/v2";
import LoadingOverlay from "../../../PackageBuilders/MultiPose/LoadingOverlay";
import useAccessCodePersister from "../../../../hooks/useAccessCodePersister";
import useRetouchingExtra from "../../../../hooks/useRetouchingExtra";
import usePricelist from "../../../../hooks/usePricelist";
import useMultiSeasonPoses from "../../../../hooks/useMultiSeasonPoses";
import usePoses from "../../../../hooks/usePoses";
import usePngPoses from "../../../../hooks/usePngPoses";
import useOfferCoupon from '../../../../hooks/useOfferCoupon';
import PromoBanner from '../../../PromoBanner';

function useOrderPackageMutation(orderPackage, selectedPricelist) {
  const [indices, setIndices] = useState([]);

  const history = useHistory();
  const { customer, setCustomer } = useCustomer();
  const { data: pricelist } = useHolidayPricelist(customer.currentSchool?.id);

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ addonSheets }) => {
      const { options } = pricelist;

      orderPackage.cartId = customer.cart.id;
      orderPackage.studentId = customer.currentStudent.id;
      orderPackage.packageId = orderPackage.packageId || selectedPricelist?.id || pricelist.id;
      orderPackage.optionId = orderPackage.optionId || options[0].id;
      orderPackage.apiOption = orderPackage.apiOption || options[0];

      const newIndices = [];

      orderPackage.addonSheets = [...orderPackage.addonSheets, ...addonSheets].map((addonSheet, i) => {
        if (addonSheet.id) return addonSheet;

        const { addon, background, pose } = addonSheet;
        const params = {
          addonId: addon.id,
          backgroundId: background.id,
          index: i,
          poseId: pose.id,
        };

        newIndices.push(i);

        return new AddonSheet(params);
      });

      await orderPackage.save({ with: ['addonSheets'] });

      setIndices(newIndices);
      setCustomer({ ...customer, currentOrderPackage: orderPackage });
    },
    mutationKey: ['orderPackage', 'holiday'],
    onSuccess: async () => {
      if (orderPackage.errors.length > 0) return;

      const sheets = orderPackage.addonSheets.filter((addonSheet) => indices.includes(addonSheet.index));

      await queryClient.invalidateQueries([QUERY_KEYS.ORDER_PACKAGES]).then(() => history.push(routes.holiday.edit(orderPackage.id, { s: sheets.map((s) => s.id) })));
    },
  });
}

function HolidayBuilder() {
  const { access_code, sch, o, optional, pid } = useQueryString();
  const { customer, setCustomer } = useCustomer();
  const history = useHistory();
  const { data: addons } = useHolidayAddons();
  const { data: selectedPricelist } = usePricelist(pid);
  const { data: pricelist } = useHolidayPricelist(customer?.currentSchool?.id);
  const { data: poses, isLoading } = selectedPricelist?.id && (selectedPricelist?.multipleSeasons?.length || selectedPricelist?.displaySharedPoses) ? (
    useMultiSeasonPoses(customer?.currentStudent?.id, selectedPricelist?.id)
  ) : (
    usePngPoses(customer.currentStudent.id, selectedPricelist?.id)
  );

  const { data: backgrounds } = useBackgrounds(pid || pricelist?.id, ['productTypeNames']);
  const { data: baseOrderPackage } = useOrderPackage(o);
  const { data: retouching, isLoading: isLoadingRetouching } = useRetouchingExtra();

  // Same promotional offer the package-selection screen shows, resolved for this
  // student + the pricelist (pid) so a senior sees their live offer (e.g. GRAD27
  // -- "20% off + 25 free grad announcements") while picking add-ons here too.
  const { data: offer } = useOfferCoupon(
    customer?.currentStudent?.studentId,
    customer?.currentSchool?.id,
    pid,
  );

  // Server-computed: does the cart currently have a senior portraits package
  // priced > $1? Gates the "Free" strike-through on announcement cards so a
  // customer who walks straight into /holiday without a package sees the real
  // price -- and matches what checkout will charge (Coupon.calculate_discount
  // uses the same predicate).
  //
  // refetchOnMount: 'always' is critical: the app-wide QueryClient sets
  // refetchOnMount: false, and nothing invalidates [QUERY_KEYS.CART, id] when
  // packages are added on other pages (the mutations invalidate ORDER_PACKAGES
  // only). Without this override, a customer who visits /holiday, adds a
  // qualifying senior package elsewhere, then returns to /holiday would see
  // the stale `qualifiesForFreeAnnouncements: false` and announcements would
  // stay at full price -- exactly the bug this branch fixes.
  const { data: cartWithQualification } = useCart(customer?.cart?.id, { refetchOnMount: 'always' });
  const qualifiesForFreeAnnouncements = !!cartWithQualification?.qualifiesForFreeAnnouncements;

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  useHolidayCoupon();

  const [orderPackage, setOrderPackage] = useState(new OrderPackage());

  const handleSkip = () => {
    if (retouching && o) {
      history.push(routes.retouching(o))
    } else {
      history.push(routes.review())
    }
  }

  useEffect(() => {
    if (!baseOrderPackage) return;

    setOrderPackage(baseOrderPackage);
  }, [baseOrderPackage?.id]);

  const { mutate, ...rest } = useOrderPackageMutation(orderPackage, selectedPricelist);

  const mutation = { mutate, ...rest };

  useHolidayCoupon();

  const onSelectBackground = (newSheets) => mutate({ addonSheets: newSheets });

  if (!addons) return null;
  if (!pricelist) return null;
  if (backgrounds.length === 0) {
    return (
      <LoadingOverlay />
    );
  }

  if (isLoading) {
    return (
      <LoadingOverlay />
    );
  }

  if (optional && isLoadingRetouching) {
    return (
      <LoadingOverlay />
    );
  }

  return (
    <div className={`${styles.page} holidayPage`}>
      <PromoBanner offer={offer} className="mb-2" />
      <h1 className={styles.header}>Announcements & Gifts</h1>
      <ItemAdder
        mutation={mutation}
        addons={addons}
        backgrounds={backgrounds}
        options={selectedPricelist?.options || pricelist.options}
        poses={poses}
        onSelectBackground={onSelectBackground}
        offer={offer}
        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
        // Senior-ness for the grad-announcement gate comes from the pricelist the
        // customer arrived with (seniors2027 -> senior), not from pose-array indexes.
        pricelistSeason={selectedPricelist?.season}
      />

      {optional && (
        <div className={styles.btnsContainer}>
          <Button variant="" onClick={handleSkip}>Continue to Checkout</Button>
        </div>
      )}
    </div>
  );
}

export default function LandableWrapper() {
  const { sch, sid, access_code: accessCode } = useQueryString();
  const { data: student } = useStudent({ studentId: sid, schoolId: sch });
  const { customer, setCustomer } = useCustomer();
  const { isLoading } = useAccessCodePersister({ accessCode, schoolId: sch });

  useHolidayPricelist(customer.currentSchool?.id);
  useHolidayAddons();

  useEffect(() => {
    if (!student) return;

    setCustomer({ ...customer, currentStudent: student, currentSchool: student.school });
  }, [student]);

  const isSchoolSet = customer?.currentSchool?.id?.toString() === sch?.toString();
  const isStudentSet = customer?.currentStudent?.studentId?.toString() === sid?.toString();

  const skipLandableCheck = !(sch || sid);

  if (accessCode && isLoading) {
    return (
      <LoadingOverlay />
    )
  }

  if (!skipLandableCheck && !(isSchoolSet && isStudentSet)) return null;

  return (
    <HolidayBuilder />
  );
}
